
import React from "react";
import { Card, CardHeader, CardContent, CardFooter, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form } from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { RegistrationSocialLogin } from "@/components/auth/register/RegistrationSocialLogin";

const signupSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters")
});

type SignupFormValues = z.infer<typeof signupSchema>;

interface SuccessModalProps {
  nursingHomeName: string;
  onClose?: () => void;
}

export function SuccessModal({ nursingHomeName, onClose }: SuccessModalProps) {
  const [showSignup, setShowSignup] = React.useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = React.useState(false);
  
  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: "",
      password: ""
    }
  });
  
  const handleSignUp = async (data: SignupFormValues) => {
    try {
      setIsLoading(true);
      
      const { error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
      });
      
      if (error) throw error;
      
      toast({
        title: "Account created",
        description: "Your account has been created successfully. Please check your email for verification."
      });
      
      // Redirect to dashboard after successful signup
      navigate("/dashboard");
      
    } catch (error: any) {
      toast({
        title: "Error creating account",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleViewInquiries = () => {
    navigate("/auth/login", { 
      state: { redirectTo: "/dashboard" } 
    });
  };
  
  return (
    <Card className="w-full max-w-lg mx-auto">
      {!showSignup ? (
        <>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-grandpal-green-lighter p-3">
                <CheckCircle className="h-12 w-12 text-grandpal-green" />
              </div>
            </div>
            <CardTitle className="text-2xl">Thanks for contacting {nursingHomeName}</CardTitle>
            <CardDescription className="text-base mt-2">
              Your inquiry has been sent to Grandpal. We'll forward it to {nursingHomeName} and help facilitate the communication.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-grandpal-beige/30 p-4 rounded-md">
              <h3 className="font-medium mb-2">What happens next?</h3>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Grandpal will send your inquiry to {nursingHomeName}</li>
                <li>We'll ensure the nursing home receives your information</li>
                <li>The nursing home will contact you directly via your provided details</li>
                <li>Grandpal will follow up with you if you don't hear back within 3 days</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium mb-2 text-blue-800">Why use Grandpal?</h3>
              <p className="text-sm text-blue-700">
                Grandpal works as an intermediary to ensure your inquiry reaches the right people at {nursingHomeName}. 
                We track all communications to make sure you get the information you need.
              </p>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button 
              variant="default" 
              className="w-full bg-grandpal-green hover:bg-grandpal-green/90"
              onClick={() => setShowSignup(true)}
            >
              Create an account to manage your inquiries
            </Button>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={handleViewInquiries}
            >
              Already have an account? Sign in
            </Button>
          </CardFooter>
        </>
      ) : (
        <>
          <CardHeader>
            <CardTitle className="text-xl">Create your Grandpal account</CardTitle>
            <CardDescription>
              Track your inquiries and get personalized nursing home recommendations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSignUp)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your email" type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input placeholder="Create a password" type="password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button 
                  type="submit" 
                  className="w-full bg-grandpal-green hover:bg-grandpal-green/90 mt-4"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    "Create Account"
                  )}
                </Button>
              </form>
            </Form>
            
            {/* Add social login options */}
            <div className="mt-6">
              <RegistrationSocialLogin />
            </div>
            
            <div className="mt-4 text-center">
              <Button 
                variant="link" 
                onClick={() => setShowSignup(false)}
                className="text-grandpal-green"
              >
                Go back
              </Button>
            </div>
          </CardContent>
        </>
      )}
    </Card>
  );
}
